import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../../application/store/useStore';
import { updateUserAsync } from '../../../../application/features/users/userThunk';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ActionTypeMapping, RoleEnum } from '../../../../application/common/enums/user-roles';
export function EditUserDialog({ openEditUserDialog, onCloseEditUser, userToEdit, onEditUser, currentUserRole, companies }) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useAppDispatch();
    // Helper function to convert string role to enum
    const convertStringToRoleEnum = (roleString) => {
        if (typeof roleString === 'number') {
            return roleString;
        }
        switch (roleString) {
            case 'SuperAdmin':
                return RoleEnum.SuperAdmin;
            case 'Admin':
                return RoleEnum.Admin;
            case 'User':
                return RoleEnum.User;
            default:
                return RoleEnum.User;
        }
    };
    // Helper function to convert enum to string (for API calls)
    const convertRoleEnumToString = (role) => {
        switch (role) {
            case RoleEnum.SuperAdmin:
                return 'SuperAdmin';
            case RoleEnum.Admin:
                return 'Admin';
            case RoleEnum.User:
                return 'User';
            default:
                return 'User';
        }
    };
    const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            phoneNo: '',
            role: RoleEnum.User,
            companyIds: []
        }
    });
    const requiredMessage = t('requiredField');
    const theme = useTheme();
    // Effect to populate form when userToEdit changes or dialog opens
    useEffect(() => {
        if (openEditUserDialog && userToEdit) {
            console.log('=== Edit User Dialog Opened ===');
            console.log('Selected user for editing:', userToEdit);
            console.log('User companyIds:', userToEdit.companyIds);
            console.log('User companies:', userToEdit.companies);
            // Convert role from string to enum if needed
            const userRole = convertStringToRoleEnum(userToEdit.role);
            console.log('Converted role:', userRole, 'from original:', userToEdit.role);
            // Ensure companyIds is an array - try multiple sources
            let userCompanyIds = [];
            if (Array.isArray(userToEdit.companyIds) && userToEdit.companyIds.length > 0) {
                userCompanyIds = userToEdit.companyIds;
            }
            else if (Array.isArray(userToEdit.companies) && userToEdit.companies.length > 0) {
                // Fallback: extract IDs from companies array if companyIds is not available
                userCompanyIds = userToEdit.companies.map((c) => c.id);
            }
            console.log('Final companyIds to set:', userCompanyIds);
            // Reset form with selected user data
            reset({
                firstName: userToEdit.firstName || '',
                lastName: userToEdit.lastName || '',
                email: userToEdit.email || '',
                phoneNo: userToEdit.phoneNo || '',
                role: userRole,
                companyIds: userCompanyIds
            });
            console.log('Form reset completed');
        }
        else if (!openEditUserDialog) {
            // Reset form when dialog closes
            reset({
                firstName: '',
                lastName: '',
                email: '',
                phoneNo: '',
                role: RoleEnum.User,
                companyIds: []
            });
        }
    }, [openEditUserDialog, userToEdit, reset]);
    // Form submission handler
    const onSubmit = async (formData) => {
        if (!userToEdit?.id) {
            console.error('No user ID found for update');
            toast.error('No user selected for update');
            return;
        }
        console.log('Updating user with data:', { ...formData, id: userToEdit.id });
        setIsSubmitting(true);
        try {
            // Convert role back to string for API call
            const updateData = {
                ...formData,
                id: userToEdit.id,
                role: convertRoleEnumToString(formData.role)
            };
            console.log('Sending update data:', updateData);
            const result = await dispatch(updateUserAsync(updateData)).unwrap();
            console.log('User updated successfully:', result);
            toast.success('User updated successfully');
            onEditUser(result);
            onCloseEditUser();
        }
        catch (error) {
            console.error('Failed to update user:', error);
            toast.error('Failed to update user');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleClose = () => {
        if (!isSubmitting) {
            onCloseEditUser();
        }
    };
    return (_jsx(_Fragment, { children: _jsxs(Dialog, { maxWidth: 'sm', fullWidth: true, sx: {
                '& .MuiDialog-paper': {
                    overflow: 'hidden'
                }
            }, open: openEditUserDialog, onClose: handleClose, children: [_jsxs(DialogTitle, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }, children: [t('editUser'), _jsx(IconButton, { "aria-label": 'close', onClick: handleClose, disabled: isSubmitting, sx: {
                                color: theme.palette.action.active
                            }, children: _jsx(CloseOutlined, {}) })] }), _jsxs("form", { onSubmit: handleSubmit(onSubmit), children: [_jsx(DialogContent, { children: _jsx(Box, { display: 'flex', flexDirection: 'column', gap: '12px', pt: 1, children: _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsxs(Grid, { size: { xs: 12, md: 6 }, children: [_jsx(Controller, { name: 'firstName', control: control, rules: {
                                                        required: {
                                                            value: true,
                                                            message: requiredMessage
                                                        }
                                                    }, render: ({ field }) => (_jsx(TextField, { fullWidth: true, label: t('firstName'), disabled: isSubmitting, ...field })) }), errors.firstName && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.firstName.message }))] }), _jsxs(Grid, { size: { xs: 12, md: 6 }, children: [_jsx(Controller, { name: 'lastName', control: control, rules: {
                                                        required: {
                                                            value: true,
                                                            message: requiredMessage
                                                        }
                                                    }, render: ({ field }) => (_jsx(TextField, { fullWidth: true, label: t('lastName'), disabled: isSubmitting, ...field })) }), errors.lastName && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.lastName.message }))] }), _jsxs(Grid, { size: { xs: 12, md: 6 }, children: [_jsx(Controller, { name: 'email', control: control, rules: {
                                                        required: {
                                                            value: true,
                                                            message: requiredMessage
                                                        },
                                                        pattern: {
                                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                            message: t('invalidEmail')
                                                        }
                                                    }, render: ({ field }) => (_jsx(TextField, { fullWidth: true, label: t('email'), type: 'email', disabled: isSubmitting, ...field })) }), errors.email && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.email.message }))] }), _jsxs(Grid, { size: { xs: 12, md: 6 }, children: [_jsx(Controller, { name: 'phoneNo', control: control, rules: {
                                                        required: {
                                                            value: true,
                                                            message: requiredMessage
                                                        }
                                                    }, render: ({ field }) => (_jsx(TextField, { fullWidth: true, label: t('phoneNumber'), disabled: isSubmitting, ...field })) }), errors.phoneNo && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.phoneNo.message }))] }), _jsxs(Grid, { size: { xs: 12 }, children: [_jsx(Controller, { name: 'companyIds', control: control, render: ({ field }) => {
                                                        // Ensure we have a valid array
                                                        const currentCompanyIds = Array.isArray(field.value) ? field.value : [];
                                                        // Find the selected company objects based on IDs
                                                        const selectedCompanies = companies.filter((company) => currentCompanyIds.includes(company.id));
                                                        console.log('Current companyIds:', currentCompanyIds);
                                                        console.log('Selected companies in render:', selectedCompanies);
                                                        return (_jsx(Autocomplete, { multiple: true, options: companies, getOptionLabel: (option) => option.name, value: selectedCompanies, onChange: (_, selectedCompanies) => {
                                                                const companyIds = selectedCompanies.map((company) => company.id);
                                                                console.log('Companies changed to:', companyIds);
                                                                field.onChange(companyIds);
                                                            }, disabled: isSubmitting, isOptionEqualToValue: (option, value) => option.id === value.id, renderInput: (params) => (_jsx(TextField, { ...params, fullWidth: true, label: t('companies') })) }));
                                                    } }), errors.companyIds && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.companyIds.message }))] }), _jsxs(Grid, { size: { xs: 12 }, children: [_jsx(Controller, { name: 'role', control: control, rules: {
                                                        required: {
                                                            value: true,
                                                            message: requiredMessage
                                                        }
                                                    }, render: ({ field }) => (_jsx(Autocomplete, { options: Object.values(RoleEnum)
                                                            .filter((value) => {
                                                            if (typeof value !== 'number')
                                                                return false;
                                                            // Show SuperAdmin role only if current user is SuperAdmin
                                                            if (value === RoleEnum.SuperAdmin && currentUserRole !== 'SuperAdmin') {
                                                                return false;
                                                            }
                                                            return true;
                                                        })
                                                            .map((role) => ({
                                                            label: ActionTypeMapping.get(role),
                                                            value: role
                                                        })), getOptionLabel: (option) => option.label, value: field.value !== undefined
                                                            ? {
                                                                label: ActionTypeMapping.get(field.value),
                                                                value: field.value
                                                            }
                                                            : null, onChange: (_, newValue) => {
                                                            field.onChange(newValue ? newValue.value : RoleEnum.User);
                                                        }, disabled: isSubmitting, renderInput: (params) => _jsx(TextField, { ...params, label: `${t('role')}*` }) })) }), errors.role && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.role.message }))] })] }) }) }), _jsxs(DialogActions, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', onClick: handleClose, disabled: isSubmitting, children: t('cancel') }), _jsx(Button, { type: 'submit', color: 'primary', variant: 'contained', disabled: isSubmitting, children: isSubmitting ? t('saving') : t('saveChanges') })] })] })] }) }));
}
